// useVoiceAvailability.ts — Pedagogical voice availability logic
//
// Voice is a pedagogical signal, not just an interface choice.
// The mic button's visibility communicates cognitive expectations:
//   - Introduction: hidden → "lean back, you're receiving"
//   - Samtale: available → "you can contribute now"
//   - Eksamen: prominent → "show what you know"
//
// Within modes, Bloom level refines further:
//   - Lower Bloom (recognize, understand): text preferred
//   - Higher Bloom (apply+): voice encouraged

export type VoiceVisibility = 'hidden' | 'available' | 'prominent';

export interface VoiceAvailabilityState {
  /** Whether voice input is available in current context */
  visibility: VoiceVisibility;
  /** Size class for the mic button */
  buttonSize: 'sm' | 'md' | 'lg';
  /** Whether to show a glow/pulse effect on the mic button */
  showGlow: boolean;
  /** Pedagogical hint explaining why voice is available/encouraged */
  hintKey: string | null;
}

interface UseVoiceAvailabilityOptions {
  teachingForm: 'introduction' | 'dialog' | 'exam';
  /** Current Bloom level for the active concept */
  currentBloomLevel?: 'recognize' | 'understand' | 'apply' | 'analyze' | 'evaluate' | 'create';
  /** Whether Anna has asked her first question (Samtale gate) */
  annaHasAskedQuestion?: boolean;
}

const BLOOM_ORDER = ['recognize', 'understand', 'apply', 'analyze', 'evaluate', 'create'];

export function useVoiceAvailability(options: UseVoiceAvailabilityOptions): VoiceAvailabilityState {
  const { teachingForm, currentBloomLevel = 'recognize' } = options;

  const bloomIndex = BLOOM_ORDER.indexOf(currentBloomLevel);
  const isHighBloom = bloomIndex >= 2; // apply or above

  switch (teachingForm) {
    case 'introduction':
      // Introduction: mic available from start. Small button, no glow.
      // At high Bloom, slightly more prominent.
      return {
        visibility: 'available',
        buttonSize: isHighBloom ? 'md' : 'sm',
        showGlow: false,
        hintKey: isHighBloom ? 'voice.hint.introHighBloom' : 'voice.hint.introAvailable',
      };

    case 'dialog':
      // Samtale: mic available from start. Prominence increases with Bloom.
      return {
        visibility: 'available',
        buttonSize: isHighBloom ? 'lg' : 'md',
        showGlow: isHighBloom,
        hintKey: isHighBloom ? 'voice.hint.dialogHighBloom' : 'voice.hint.dialogAvailable',
      };

    case 'exam':
      // Eksamen: mic is always prominent. This is where the student demonstrates.
      return {
        visibility: 'prominent',
        buttonSize: 'lg',
        showGlow: true,
        hintKey: bloomIndex >= 4
          ? 'voice.hint.examMastery'    // evaluate/create: "Show what you know"
          : 'voice.hint.examPractice',  // apply/analyze: "Practice explaining"
      };
  }
}
